/**
 * ana.js — ANA Alcazar Theme Customizing
 * ============================================================================
 * Diese Datei enthaelt alle ANA-spezifischen JS-Anpassungen, die ueber das
 * Vanilla-Symmetry-Theme hinausgehen. main.js bleibt reines Vanilla Symmetry
 * 8.3.1; ana.js wird in layout/theme.liquid NACH main.js mit `defer` geladen.
 *
 * Inhalt:
 *   1. <product-inventory>  Custom-Element  (ANA-only — nicht in Vanilla 8.3.1)
 *   2. <variant-content>    Custom-Element  (ANA-only — nicht in Vanilla 8.3.1)
 *
 * Loading-Reihenfolge:
 *   - main.js (Vanilla 8.3.1) zuerst
 *   - ana.js (diese Datei) danach
 *
 * Konfliktschutz:
 *   - Beide Custom-Elements werden mit `customElements.get()` gegen doppelte
 *     Registrierung abgesichert. Falls Vanilla in einer spaeteren Version
 *     gleichnamige Elemente einfuehrt, wird die ANA-Version NICHT registriert
 *     (TODO PHASE 3 — QA: pruefen).
 *
 * Verwendete Globals (aus main.js):
 *   - window.initLazyScript
 * ============================================================================
 */

(() => {
  /**
   * <product-inventory>
   * --------------------------------------------------------------------------
   * ANA-Custom-Element fuer die Anzeige der Lagerbestand-Notice auf PDP.
   * Reagiert auf 'on:variant:change' und blendet einen lagerbasierten
   * Hinweistext ein/aus. Erwartet Inventory-Daten als JSON in
   * <script type="application/json"> innerhalb des Elements.
   *
   * Verwendung:
   *   - snippets/product-inventory.liquid
   *   - sections/main-product.liquid / main-product-ana.liquid
   *   - sections/featured-product.liquid / featured-product-ana.liquid
   *   - snippets/cart-item.liquid, product-label-detail.liquid (CSS-Klassen)
   */
  if (!customElements.get('product-inventory')) {
    class ProductInventory extends HTMLElement {
      constructor() {
        super();
        window.initLazyScript(this, this.initLazySection.bind(this));
      }

      initLazySection() {
        this.threshold = parseInt(this.dataset.threshold, 10);
        this.productInventory = this.querySelector('.product-inventory');
        this.inventoryNotice = this.querySelector('.product-inventory__status');
        this.variantInventory = this.getVariantInventory();

        this.closest('.js-product').addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      /**
       * Gets the inventory data for all product variants
       * @returns {?object}
       */
      getVariantInventory() {
        const dataEl = this.querySelector('[type="application/json"]');
        return this.variantInventory || JSON.parse(dataEl.textContent);
      }

      /**
       * Handles a 'change' event on the variant picker element
       * @param {object} evt - Event object
       */
      handleVariantChange(evt) {
        this.updateInventory(
          evt.detail.variant
            ? this.variantInventory.find((v) => v.id === evt.detail.variant.id)
            : null
        );
      }

      /**
       *
       * Updates the inventory notice
       * @param {?object} inventory - the inventory data
       */
      updateInventory(inventory) {
        if (!inventory) {
          this.productInventory.hidden = true;
          return;
        }

        const count = inventory.inventory_quantity;
        const showCount = this.dataset.showInventoryCount === 'always'
          || (
            this.dataset.showInventoryCount === 'low'
            && count <= this.threshold
          );

        let notice = null;
        if (showCount) {
          if (count <= this.threshold) {
            notice = this.dataset.textXLeftLow.replace('[QTY]', count);
          } else {
            notice = this.dataset.textXLeftOk.replace('[QTY]', count);
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (count <= this.threshold) {
            notice = this.dataset.textLow;
          } else {
            notice = this.dataset.textOk;
          }
        }

        this.productInventory.classList.toggle('product-inventory--low', count <= this.threshold);
        this.productInventory.classList.toggle('product-inventory--ok', count > this.threshold);
        this.inventoryNotice.innerText = notice;
        this.productInventory.hidden = false;
      }
    }

    customElements.define('product-inventory', ProductInventory);
  }

  /**
   * <variant-content>
   * --------------------------------------------------------------------------
   * ANA-Custom-Element fuer variantenabhaengige Inhalte auf PDP.
   * Wechselt Inhalts-Snippets basierend auf der ausgewaehlten Variante.
   * Innerhalb des Elements stehen mehrere Templates mit data-variant=ID,
   * von denen jeweils das passende geklont und eingefuegt wird.
   *
   * Verwendung:
   *   - sections/main-product.liquid / main-product-ana.liquid
   *   - sections/featured-product.liquid / featured-product-ana.liquid
   */
  if (!customElements.get('variant-content')) {
    class VariantContent extends HTMLElement {
      constructor() {
        super();
        if (this.childElementCount > 0) {
          this.closest('.js-product').addEventListener('on:variant:change', this.handleVariantChange.bind(this));
        }
      }

      /**
       * Handles a 'change' event on the variant picker element
       * @param {object} evt - Event object
       */
      handleVariantChange(evt) {
        [...this.childNodes].filter((el) => el.tagName !== 'SCRIPT').forEach((el) => el.remove());
        this.querySelectorAll(':not(script)').forEach((el) => el.remove());
        const contentToShow = this.querySelector(`[data-variant="${evt.detail.variant ? evt.detail.variant.id : ''}"]`);
        if (contentToShow) {
          this.insertAdjacentHTML('beforeend', contentToShow.innerHTML);
        }
      }
    }

    customElements.define('variant-content', VariantContent);
  }
})();


/**
 * ----------------------------------------------------------------------------
 * 3. Blog-Navigation: Uebersetzung + Reihenfolge   (ANA-only)
 * ----------------------------------------------------------------------------
 * Hintergrund:
 * Die Blog-Navigation wird von der App "Bloggle" in
 * sections/main-bloggle-blog.liquid erzeugt. Die App bietet pro Link nur EIN
 * Beschriftungsfeld — es gibt dort keine Uebersetzungen. Jeder Patch direkt in
 * der Section-Datei wird von der App beim naechsten Regenerieren geloescht
 * (nachweislich am 16.08.2026 passiert).
 *
 * Deshalb laeuft die Uebersetzung hier, in einer ANA-eigenen Datei, die Bloggle
 * nicht anfasst.
 *
 * Voraussetzung (Bloggle-App > Blog List layout > Blog Navigation):
 *   Manueller Link  Url: /blogs/magazin   Url anchor: Uebersicht
 *
 * Zuordnung ueber die href, nicht ueber den Linktext — dadurch unabhaengig
 * davon, wie die Labels in der App benannt oder sortiert sind.
 */
(() => {
  const LABELS = {
    magazin:  { de: 'Übersicht', en: 'Overview',  nl: 'Overzicht' },
    styling:  { de: 'Styling',   en: 'Styling',   nl: 'Styling' },
    ratgeber: { de: 'Ratgeber',  en: 'Guide',     nl: 'Advies' },
    anlass:   { de: 'Anlass',    en: 'Occasion',  nl: 'Gelegenheid' }
  };

  const keyFromHref = (href) => {
    if (!href) return null;
    const path = href.split('?')[0].split('#')[0].replace(/\/+$/, '');
    if (/\/tagged\/styling$/.test(path)) return 'styling';
    if (/\/tagged\/ratgeber$/.test(path)) return 'ratgeber';
    if (/\/tagged\/anlass$/.test(path)) return 'anlass';
    if (/\/blogs\/magazin$/.test(path)) return 'magazin';
    return null;
  };

  const applyBlogNav = () => {
    const nav = document.querySelector('.bloggle--nav');
    if (!nav) return;

    const lang = (document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
    const items = [...nav.querySelectorAll('.bloggle--nav-item')];
    if (!items.length) return;

    let overview = null;

    items.forEach((item) => {
      const key = keyFromHref(item.getAttribute('href'));
      if (!key) return;

      // Beschriftung nur ersetzen, wenn fuer die Sprache eine hinterlegt ist
      const label = LABELS[key][lang];
      if (label) item.textContent = label;

      if (key === 'magazin') overview = item;
    });

    // "Uebersicht" nach vorne holen — Bloggle haengt manuelle Links hinten an
    if (overview && nav.firstElementChild !== overview) {
      nav.insertBefore(overview, nav.firstElementChild);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyBlogNav, { once: true });
  } else {
    applyBlogNav();
  }
})();
